/**
 * CouponInput.jsx
 *
 * Self-contained coupon code input component.
 *
 * Responsibilities:
 *   - Render the coupon input field and Apply button.
 *   - Call the validate-coupon Edge Function directly via fetch
 *     (using the same direct-fetch pattern as createPaymentIntentWithAuthRetry
 *     so we always get the real error body, not a swallowed invoke error).
 *   - Display validation errors inline.
 *   - Show the applied coupon badge with a Remove button.
 *   - Call onCouponApplied(couponData) when a valid coupon is confirmed.
 *   - Call onCouponRemoved() when the coupon is cleared.
 *
 * Props:
 *   packageId        {string}   – The selected package ID (starter | popular | pro)
 *   appliedCoupon    {object|null} – Currently applied coupon data (controlled)
 *   onCouponApplied  {function} – Called with { discountAmount, discountLabel, finalAmount, promotionCodeId }
 *   onCouponRemoved  {function} – Called when coupon is removed
 *   disabled         {boolean}  – Disables the input (e.g. while payment is processing)
 */

import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;
const ANON_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY;

/**
 * Calls validate-coupon via direct fetch so the real error body is always
 * readable (supabase.functions.invoke swallows non-2xx bodies).
 *
 * @param {string} code       – Promotion code string
 * @param {string} packageId  – Package ID
 * @param {string} token      – Supabase access token
 * @returns {Promise<{ ok: boolean, data?: object, error?: string }>}
 */
async function callValidateCoupon(code, packageId, token) {
  if (!SUPABASE_URL || !ANON_KEY) {
    return { ok: false, error: 'Payment service is not configured. Please contact support.' };
  }

  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/validate-coupon`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': ANON_KEY,
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ code, packageId }),
    });

    let json = null;
    try {
      json = await res.json();
    } catch {
      return { ok: false, error: `Server error (HTTP ${res.status}). Please try again.` };
    }

    if (!res.ok) {
      return { ok: false, error: json?.error || `Server error (HTTP ${res.status}). Please try again.` };
    }

    return { ok: true, data: json };
  } catch (err) {
    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
    return {
      ok: false,
      error: isOffline
        ? 'No internet connection. Please check your network and try again.' :'Unable to reach the coupon service. Please try again.',
    };
  }
}

const CouponInput = ({
  packageId,
  appliedCoupon,
  onCouponApplied,
  onCouponRemoved,
  disabled = false,
  supabase,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    setInputValue(e?.target?.value?.toUpperCase() ?? '');
    if (error) setError('');
  };

  const handleApply = async () => {
    const code = inputValue?.trim();
    if (!code) {
      setError('Please enter a coupon code.');
      return;
    }

    if (!packageId) {
      setError('No package selected. Please go back and select a package.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Get the current session token for the Edge Function call.
      const { data: sessionData, error: sessionError } = await supabase?.auth?.getSession();
      const token = sessionData?.session?.access_token;

      if (sessionError || !token) {
        setError('Your session has expired. Please sign in again.');
        return;
      }

      const { ok, data, error: fetchError } = await callValidateCoupon(code, packageId, token);

      if (!ok) {
        setError(fetchError || 'Unable to validate coupon. Please try again.');
        return;
      }

      // Edge Function returned 200 but coupon is not valid
      if (!data?.valid) {
        setError(data?.error || 'Invalid coupon code.');
        return;
      }

      // Coupon is valid — pass the data up to the parent
      onCouponApplied?.({
        code,
        discountAmount: data.discountAmount,
        discountLabel: data.discountLabel,
        finalAmount: data.finalAmount,
        originalAmount: data.originalAmount,
        promotionCodeId: data.promotionCodeId,
      });

      // Clear the input after successful application
      setInputValue('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemove = () => {
    setInputValue('');
    setError('');
    onCouponRemoved?.();
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter') {
      e.preventDefault();
      handleApply();
    }
  };

  // ── Applied state ──────────────────────────────────────────────────────────
  if (appliedCoupon) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="CheckCircle" size={16} color="#10b981" />
          <div>
            <p className="text-sm font-semibold text-emerald-800">{appliedCoupon.code}</p>
            <p className="text-xs text-emerald-600">{appliedCoupon.discountLabel} applied</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleRemove}
          disabled={disabled}
          className="text-emerald-600 hover:text-emerald-800 transition-colors disabled:opacity-40"
          title="Remove coupon"
          aria-label="Remove coupon"
        >
          <Icon name="X" size={16} />
        </button>
      </div>
    );
  }

  // ── Input state ────────────────────────────────────────────────────────────
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-2">
        <span className="flex items-center gap-1.5">
          <Icon name="Ticket" size={15} color="#9333EA" />
          Discount Coupon
        </span>
      </label>
      <div className="flex gap-2">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Enter coupon code"
          disabled={disabled || isLoading}
          className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent uppercase disabled:opacity-50"
          aria-label="Coupon code"
          autoComplete="off"
          spellCheck={false}
        />
        <button
          type="button"
          onClick={handleApply}
          disabled={disabled || isLoading || !inputValue?.trim()}
          className="px-3 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1 min-w-[64px] justify-center"
        >
          {isLoading ? (
            <Icon name="Loader" size={14} className="animate-spin" />
          ) : (
            'Apply'
          )}
        </button>
      </div>
      {error && (
        <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1" role="alert">
          <Icon name="AlertCircle" size={12} color="#DC2626" />
          {error}
        </p>
      )}
    </div>
  );
};

export default CouponInput;
